import React from 'react';
import { Box, Text, VStack, Image, IconButton } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCamera, FiX } from 'react-icons/fi';
import webCore from '@happylife-a/web-core';

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);
const MotionIcon = motion.div;

export default function UploadArea({
  isLoading,
  selectedFile,
  previewUrl,
  isDragOver,
  borderColor,
  onRemoveImage,
  onDrop,
  onDragOver,
  onDragLeave,
  onClick,
}) {
  const { t } = webCore.contexts.useTranslation();
  const uploadAreaVariants = {
    normal: {
      scale: 1,
      borderColor: borderColor,
      transition: { duration: 0.2 },
    },
    hover: {
      scale: 1.02,
      borderColor: 'rgba(255, 255, 255, 0.8)',
      transition: { duration: 0.2 },
    },
    drag: {
      scale: 1.05,
      borderColor: 'rgba(255, 255, 255, 1)',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      transition: { duration: 0.2 },
    },
  };

  return (
    <MotionBox
      w="100%"
      maxW="600px"
      h="200px"
      border="2px dashed"
      borderColor={borderColor}
      borderRadius="lg"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor={!selectedFile ? 'pointer' : 'default'}
      padding={4}
      position="relative"
      overflow="hidden"
      variants={uploadAreaVariants}
      initial="normal"
      animate={isDragOver ? 'drag' : 'normal'}
      whileHover={!selectedFile ? 'hover' : 'normal'}
      onClick={onClick}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      <AnimatePresence mode="wait">
        {selectedFile && previewUrl ? (
          <MotionBox
            key="preview"
            w="100%"
            h="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            <Image
              src={previewUrl}
              alt="Product preview"
              height="100%"
              maxW="600px"
              objectFit="contain"
              borderRadius="lg"
            />
            {!isLoading && (
              <IconButton
                icon={<FiX />}
                aria-label="Remove image"
                position="absolute"
                top={2}
                right={2}
                size="sm"
                colorScheme="red"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveImage();
                }}
                zIndex={1}
              />
            )}
          </MotionBox>
        ) : (
          <MotionVStack
            key="upload"
            spacing={3}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <MotionIcon
              style={{
                color: 'white',
                fontSize: '32px',
              }}
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.2 }}
            >
              <FiCamera />
            </MotionIcon>
            <Text color="white" fontSize="md" fontWeight="medium">
              {t('Drop image here or click to upload')}
            </Text>
            <Text color="white" fontSize="sm" opacity={0.8}>
              {t('JPG, PNG, WebP (max 10MB)')}
            </Text>
          </MotionVStack>
        )}
      </AnimatePresence>
    </MotionBox>
  );
}
